import { Link } from "react-router-dom";
import useScrollReveal from "@/hooks/useScrollReveal";

interface ServiceAreasProps {
  tagline: string;
  title: string;
  subtitle: string;
  areas: string[];
  note: string;
}

export default function ServiceAreas({ tagline, title, subtitle, areas, note }: ServiceAreasProps) {
  const headRef = useScrollReveal<HTMLDivElement>();
  const mapRef = useScrollReveal<HTMLDivElement>();
  const listRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-cream py-32 px-6 lg:px-14 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div ref={headRef} className="sr-hidden mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <span className="gold-rule" />
              <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.22em]">{tagline}</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-charcoal font-bold leading-tight max-w-xl">
              {title}
            </h2>
          </div>
          <p className="text-stone-500 font-sans text-sm leading-relaxed max-w-sm lg:text-right">
            {subtitle}
          </p>
        </div>

        {/* Map — full width, prominent */}
        <div ref={mapRef} className="sr-hidden mb-6 relative">
          <div
            className="w-full overflow-hidden rounded-lg border border-stone-200 relative"
            style={{ height: "520px" }}
          >
            <iframe
              title="Glen Davis and Associates Photography – 709 Shore Dr, Bremerton WA"
              src="https://maps.google.com/maps?q=709+Shore+Dr+Bremerton+WA&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* Overlay label on top-left of map */}
            <div className="absolute top-4 left-4 bg-charcoal/90 backdrop-blur-sm text-white px-4 py-2.5 rounded-md flex items-center gap-2.5 border border-stone-700 pointer-events-none">
              <div className="w-5 h-5 flex items-center justify-center text-gold">
                <i className="ri-map-pin-fill text-sm"></i>
              </div>
              <div>
                <p className="font-sans font-bold text-xs">Glen Davis Photography</p>
                <p className="font-sans text-stone-400 text-[10px]">709 Shore Dr · Bremerton, WA</p>
              </div>
            </div>
          </div>

          {/* Address bar below map */}
          <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white border border-stone-200 rounded-lg px-6 py-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30 text-gold flex-shrink-0">
                <i className="ri-map-pin-2-line text-base"></i>
              </div>
              <div>
                <p className="text-charcoal font-sans text-sm font-bold">709 Shore Dr, Bremerton, WA</p>
                <p className="text-stone-400 font-sans text-xs mt-0.5">Glen Davis and Associates Photography · Kitsap County</p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <a
                href="tel:360-204-4747"
                className="inline-flex items-center gap-2 text-charcoal font-sans text-xs font-bold uppercase tracking-widest hover:text-gold transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                360-204-4747
              </a>
              <span className="text-stone-300 hidden sm:block">|</span>
              <a
                href="https://maps.app.goo.gl/fb7msCbj7DyRFWRr9"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 text-gold font-sans text-xs font-bold uppercase tracking-widest hover:text-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Directions
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-xs"></i>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Service area pills + note */}
        <div ref={listRef} className="sr-hidden sr-delay-2 flex flex-col gap-6">
          <div className="flex flex-wrap gap-2.5">
            {areas.map((area, i) => (
              <span
                key={i}
                className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-md border border-stone-200 hover:border-gold/50 transition-colors text-stone-700 font-sans text-xs font-semibold cursor-default"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center text-gold">
                  <i className="ri-map-pin-fill text-xs"></i>
                </div>
                {area}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-stone-400 font-sans text-xs italic leading-relaxed max-w-lg">{note}</p>
            <Link
              to="/location"
              className="inline-flex items-center gap-2 text-charcoal font-sans text-xs font-bold uppercase tracking-widest hover:text-gold transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              Full Location Details
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
